import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import {
  Building2,
  Settings,
  BarChart3,
  Coins,
  ClipboardList,
  SlidersHorizontal,
  Grid3x3,
  FileText,
  ChefHat,
  QrCode,
  Megaphone,
  Receipt,
  LineChart,
  Link2,
  User,
  CreditCard,
  Mail,
  Pencil,
  Home,
  type LucideIcon,
} from 'lucide-react';

/* Styling uses dining landing tokens: navy, gold, slate (see LANDING_* in LandingPage) */

const PART1: { icon: LucideIcon; title: string; desc: string }[] = [
  {
    icon: Home,
    title: 'Personal dashboard',
    desc: 'See every unit you belong to, open invitations, and jump to the next event or RSVP that needs you.',
  },
  {
    icon: Building2,
    title: 'Unit dashboard',
    desc: 'A single home for the unit: upcoming and past events, members, and shortcuts to create events or open settings.',
  },
  {
    icon: Settings,
    title: 'Unit settings',
    desc: 'Configure venues, membership, roles, and other defaults that every event in the unit can use.',
  },
  {
    icon: BarChart3,
    title: 'Unit analytics',
    desc: 'High-level numbers across events so stewards and officers can see trends for the whole unit.',
  },
  {
    icon: Coins,
    title: 'Masonic Tools credits',
    desc: 'A shared unit balance for platform usage: see history, buy credit packs, and keep exports flowing when the balance is healthy.',
  },
];

const PART2_HEADLINE: { icon: LucideIcon; title: string; desc: string }[] = [
  {
    icon: ClipboardList,
    title: 'Event dashboard',
    desc: 'The master RSVP list with sorting, payment status, revenue, interest-only mode, and shareable links.',
  },
  {
    icon: SlidersHorizontal,
    title: 'Event settings',
    desc: 'Tickets, pricing, meal options, questionnaire fields, deadlines, and credit usage per RSVP.',
  },
];

const PART2_GROUPS: { groupLabel: string; items: { icon: LucideIcon; title: string; desc: string }[] }[] = [
  {
    groupLabel: 'Seating, print, and kitchen',
    items: [
      {
        icon: Grid3x3,
        title: 'Table planner',
        desc: 'A visual table layout so you can place guests, balance tables, and prepare for the room.',
      },
      {
        icon: FileText,
        title: 'Place cards and PDFs',
        desc: 'Design and print place cards and other PDF outputs tailored to the event and seating plan.',
      },
      {
        icon: ChefHat,
        title: 'Kitchen and booking form',
        desc: 'A chef-facing report and booking-style summary of choices and numbers for the kitchen and caterers.',
      },
    ],
  },
  {
    groupLabel: 'Arrival and comms',
    items: [
      {
        icon: QrCode,
        title: 'Check-in and attendance',
        desc: 'QR-based check-in on the night, plus a full attendance list and printable views for the Tyler or steward.',
      },
      {
        icon: Megaphone,
        title: 'Announcements',
        desc: 'Send event announcements and updates to your guest list with clear, controlled comms.',
      },
    ],
  },
  {
    groupLabel: 'Payments and insight',
    items: [
      {
        icon: Receipt,
        title: 'Payments and invoices',
        desc: 'Event-level view of who has paid, who owes, and access to the payments workspace for invoices and reminders.',
      },
      {
        icon: LineChart,
        title: 'Event analytics',
        desc: 'Charts and stats for a single event so you can review attendance, takings, and patterns after the fact.',
      },
    ],
  },
];

const PART3: { icon: LucideIcon; title: string; desc: string }[] = [
  {
    icon: Link2,
    title: 'RSVP and secure links',
    desc: 'Guests complete tickets, dietary and questionnaire answers, and get a stable link to return to their booking later.',
  },
  {
    icon: User,
    title: 'My RSVP dashboard',
    desc: 'Change contact details, ticket and meal choices, add or remove guest places, and follow waitlist or payment steps in one place.',
  },
  {
    icon: CreditCard,
    title: 'Pay online and group pay',
    desc: 'Pay by card, bank, or other methods the unit enables; group payment flows when one person covers several guests.',
  },
  {
    icon: Mail,
    title: 'Invitations and interest',
    desc: 'Email invitations and tokenised links; interest-only and waitlist paths before places are confirmed.',
  },
  {
    icon: Pencil,
    title: 'Edit and refine',
    desc: 'Targeted edit flows for RSVP details and tickets so guests can correct their booking when organisers allow it.',
  },
];

function CardList({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col gap-px overflow-hidden rounded-[14px] border border-slate-200/90 bg-slate-200/90">
      {children}
    </div>
  );
}

function OverviewHCard({ icon: Icon, title, desc }: { icon: LucideIcon; title: string; desc: string }) {
  return (
    <div className="flex items-start gap-4 sm:gap-5 bg-white px-5 py-5 sm:px-6 sm:py-[22px] transition-colors duration-150 hover:bg-slate-50/80">
      <div
        className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gold/10 sm:h-10 sm:w-10"
        aria-hidden
      >
        <Icon className="h-4 w-4 text-gold sm:h-[18px] sm:w-[18px]" strokeWidth={1.6} />
      </div>
      <div className="min-w-0">
        <p className="mb-1 text-base font-medium tracking-[-0.01em] text-navy sm:text-[17px]">{title}</p>
        <p className="text-pretty text-sm leading-[1.7] text-slate-700 sm:text-[15px] sm:leading-[1.75]">{desc}</p>
      </div>
    </div>
  );
}

function FeatCard({ icon: Icon, title, desc }: { icon: LucideIcon; title: string; desc: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-4 rounded-[14px] bg-navy p-5 sm:p-6">
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gold/15 sm:h-10 sm:w-10"
        aria-hidden
      >
        <Icon className="h-4 w-4 text-gold sm:h-[18px] sm:w-[18px]" strokeWidth={1.6} />
      </div>
      <div>
        <p className="text-pretty mb-1.5 text-base font-medium tracking-[-0.01em] text-stone-100 sm:text-[17px]">
          {title}
        </p>
        <p className="text-pretty text-sm leading-[1.75] text-slate-300/95 sm:text-[15px]">{desc}</p>
      </div>
    </div>
  );
}

function Ornament() {
  return (
    <div className="flex items-center gap-3.5 py-0" aria-hidden>
      <div className="h-px flex-1 bg-slate-200/80" />
      <div className="h-1.5 w-1.5 shrink-0 rotate-45 bg-gold/50" />
      <div className="h-px flex-1 bg-slate-200/80" />
    </div>
  );
}

type SectionBlockProps = {
  partNum: string;
  title: string;
  description: string;
  children: ReactNode;
  last?: boolean;
};

function SectionBlock({ partNum, title, description, children, last = false }: SectionBlockProps) {
  return (
    <div className={cn('section py-12 sm:py-16', last && 'pb-0')}>
      <p className="mb-3 text-xs font-medium uppercase tracking-[0.12em] text-gold sm:mb-3.5 sm:text-sm sm:tracking-[0.14em]">
        {partNum}
      </p>
      <h2 className="font-overview-serif mb-3.5 text-[1.75rem] font-normal leading-[1.25] tracking-[-0.015em] text-navy sm:text-3xl sm:leading-tight">
        {title}
      </h2>
      <p className="text-pretty mb-8 max-w-none text-base leading-[1.75] text-slate-700 sm:mb-10 sm:text-lg sm:leading-[1.7]">
        {description}
      </p>
      {children}
    </div>
  );
}

export function MasonicToolsOnboardingGuide() {
  return (
    <div className="w-full px-0 pt-12 pb-10 font-sans text-slate-900 sm:pt-20 sm:pb-12 sm:text-base">
      <div className="inner mx-auto w-full max-w-full">
        <div className="mb-14 text-center sm:mb-20">
          <p className="mb-3 text-xs font-medium uppercase tracking-[0.14em] text-gold sm:mb-4 sm:text-sm sm:tracking-[0.18em]">
            Platform overview
          </p>
          <h2
            id="app-features-heading"
            className="font-overview-serif mb-4 text-balance text-4xl font-normal leading-[1.15] tracking-[-0.02em] text-navy sm:mb-5 sm:text-5xl"
          >
            How Masonic Tools works
          </h2>
          <p className="text-pretty mx-auto max-w-none text-base leading-[1.75] text-slate-700 sm:text-lg sm:leading-[1.7]">
            Three connected layers &mdash; your unit, each event, and the guest experience &mdash; each with its own
            set of tools.
          </p>
        </div>

        <SectionBlock
          partNum="Part 1 of 3"
          title="Your unit and account"
          description="The personal dashboard, unit home, and unit settings are where you switch between units, see invitations, and run the Lodge or Chapter as a whole."
        >
          <CardList>
            {PART1.map((row) => (
              <OverviewHCard key={row.title} icon={row.icon} title={row.title} desc={row.desc} />
            ))}
          </CardList>
        </SectionBlock>

        <Ornament />

        <SectionBlock
          partNum="Part 2 of 3"
          title="Each event: organiser tools"
          description="The event dashboard is the control room: RSVPs, money, and production. Deeper tools open from the same event &mdash; from table plans to the booking form."
        >
          <p className="mb-3 pl-0.5 text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500 sm:mb-3.5 sm:text-xs">
            Headline tools
          </p>
          <div className="mb-8 grid grid-cols-1 gap-3 sm:mb-10 min-[500px]:grid-cols-2 min-[500px]:gap-4">
            {PART2_HEADLINE.map((row) => (
              <FeatCard key={row.title} icon={row.icon} title={row.title} desc={row.desc} />
            ))}
          </div>

          {PART2_GROUPS.map((group, gi) => (
            <div
              key={group.groupLabel}
              className={cn('sub-group', gi < PART2_GROUPS.length - 1 ? 'mb-3' : 'mb-0')}
            >
              <p className="mb-3 pl-0.5 text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500 sm:mb-3.5 sm:text-xs">
                {group.groupLabel}
              </p>
              <CardList>
                {group.items.map((row) => (
                  <OverviewHCard key={row.title} icon={row.icon} title={row.title} desc={row.desc} />
                ))}
              </CardList>
            </div>
          ))}
        </SectionBlock>

        <Ornament />

        <SectionBlock
          partNum="Part 3 of 3"
          title="Members and guests: RSVP experience"
          description="After using the public RSVP link, the My RSVP dashboard is where a guest or member updates their own booking without touching the organiser list."
          last
        >
          <CardList>
            {PART3.map((row) => (
              <OverviewHCard key={row.title} icon={row.icon} title={row.title} desc={row.desc} />
            ))}
          </CardList>
        </SectionBlock>
      </div>
    </div>
  );
}
